import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Eye, EyeOff, Quote } from "lucide-react";
import { api, errorText } from "@/lib/adminApi";

const inputCls = "mt-2 w-full rounded-xl border border-[#D4AF37]/35 bg-[#0A0B0E] px-4 py-3 text-base text-[#F9F8F6] focus:border-[#F3C649]";
const EMPTY = { quote: "", name: "", role: "" };

const AddForm = ({ onAdd }) => {
  const [v, setV] = useState(EMPTY);
  const [approved, setApproved] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setV({ ...v, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!v.quote.trim() || !v.name.trim()) { toast.error("Add the testimonial text and the person's name."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/testimonials", { ...v, role: v.role || null, published: true });
      onAdd(data);
      setV(EMPTY);
      setApproved(false);
      toast.success("Testimonial published to Resources");
    } catch (err) {
      toast.error(errorText(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} aria-label="Add testimonial" className="rounded-3xl border border-[#D4AF37]/25 bg-[#12141C] p-6 sm:p-8" data-testid="testimonial-form">
      <h2 className="font-display text-3xl font-semibold">Add an approved testimonial</h2>
      <div className="mt-6 space-y-5">
        <div>
          <label htmlFor="t-quote" className="text-sm font-semibold">Testimonial</label>
          <textarea id="t-quote" rows={4} value={v.quote} onChange={set("quote")} className={inputCls} data-testid="testimonial-quote-input" />
        </div>
        <div className="grid gap-5 sm:grid-cols-2">
          <div>
            <label htmlFor="t-name" className="text-sm font-semibold">Name</label>
            <input id="t-name" value={v.name} onChange={set("name")} className={inputCls} data-testid="testimonial-name-input" />
          </div>
          <div>
            <label htmlFor="t-role" className="text-sm font-semibold">Role or organization <span className="font-normal text-[#C8C5BB]">(optional)</span></label>
            <input id="t-role" value={v.role} onChange={set("role")} className={inputCls} data-testid="testimonial-role-input" />
          </div>
        </div>
        <label className="flex items-start gap-3 text-sm text-[#E6E3DA]">
          <input type="checkbox" checked={approved} onChange={(e) => setApproved(e.target.checked)} className="mt-1 h-4 w-4 accent-[#F3C649]" data-testid="testimonial-approved-checkbox" />
          This person has approved this testimonial, and their name, for public display.
        </label>
      </div>
      <button type="submit" disabled={!approved || busy} className="btn-gold mt-6 disabled:cursor-not-allowed disabled:opacity-50" data-testid="testimonial-submit-btn">Publish testimonial</button>
    </form>
  );
};

const Row = ({ t, onToggle, onDelete }) => (
  <li className="rounded-3xl border border-[#D4AF37]/25 bg-[#12141C] p-6" data-testid={`testimonial-${t.id}`}>
    <div className="flex items-start gap-4">
      <Quote size={22} aria-hidden="true" className="mt-1 shrink-0 text-[#F3C649]" />
      <div className="min-w-0 flex-1">
        <p className="text-[#F9F8F6]">{t.quote}</p>
        <p className="mt-3 text-sm text-[#C8C5BB]">— {t.name}{t.role ? `, ${t.role}` : ""}</p>
      </div>
    </div>
    <div className="mt-5 flex flex-wrap items-center gap-3">
      <span className={`rounded-full px-3 py-1 text-xs font-semibold ${t.published ? "bg-[#F3C649] text-[#0A0B0E]" : "border border-white/20 text-[#C8C5BB]"}`} data-testid={`testimonial-state-${t.id}`}>
        {t.published ? "Shown on Resources" : "Hidden"}
      </span>
      <button type="button" onClick={() => onToggle(t)} className="btn-ghost !px-4 !py-2 text-sm" data-testid={`testimonial-toggle-${t.id}`}>
        {t.published ? <EyeOff size={15} aria-hidden="true" /> : <Eye size={15} aria-hidden="true" />}{t.published ? "Hide" : "Show"}
      </button>
      <button type="button" onClick={() => onDelete(t)} className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm text-[#FF9C8A] hover:bg-[#FF9C8A]/10" data-testid={`testimonial-delete-${t.id}`}>
        <Trash2 size={15} aria-hidden="true" /> Delete<span className="sr-only"> testimonial from {t.name}</span>
      </button>
    </div>
  </li>
);

export const TestimonialsManager = () => {
  const [items, setItems] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/admin/testimonials").then(({ data }) => { if (alive) setItems(Array.isArray(data) ? data : []); }).catch(() => { if (alive) setItems([]); });
    return () => { alive = false; };
  }, []);

  const onToggle = async (t) => {
    try {
      const { data } = await api.patch(`/admin/testimonials/${t.id}`, { published: !t.published });
      setItems((xs) => xs.map((x) => (x.id === t.id ? data : x)));
      toast.success(data.published ? "Now shown on Resources" : "Hidden from Resources");
    } catch (e) { toast.error(errorText(e.response?.data?.detail)); }
  };

  const onDelete = async (t) => {
    if (!window.confirm(`Delete the testimonial from ${t.name}?`)) return;
    try {
      await api.delete(`/admin/testimonials/${t.id}`);
      setItems((xs) => xs.filter((x) => x.id !== t.id));
      toast.success("Testimonial deleted");
    } catch (e) { toast.error(errorText(e.response?.data?.detail)); }
  };

  return (
    <div className="mt-10 grid gap-8 lg:grid-cols-12" data-testid="testimonials-manager">
      <div className="lg:col-span-5"><AddForm onAdd={(t) => setItems((xs) => [t, ...(xs ?? [])])} /></div>
      <div className="lg:col-span-7">
        {items === null && <p className="text-[#C8C5BB]">Loading…</p>}
        {items?.length === 0 && <p className="rounded-3xl border border-dashed border-[#D4AF37]/40 p-10 text-center text-[#C8C5BB]" data-testid="testimonials-empty">No testimonials yet. Resources shows a placeholder until you add one.</p>}
        <ul className="space-y-4">{(items ?? []).map((t) => <Row key={t.id} t={t} onToggle={onToggle} onDelete={onDelete} />)}</ul>
      </div>
    </div>
  );
};
